/**
 * Matrice de gain de la tresorerie disponible.
 *
 * La matrice montre a l'adherent ce que deviendrait sa tresorerie disponible si
 * son prix de vente et son rendement bougeaient de plus ou moins 10 et 20 %.
 * Ses propres valeurs occupent la troisieme position des deux axes.
 *
 * Les montants sont ceux du serveur, recopies case a case. Le front les avait
 * un temps RECALCULES a partir d'axes arrondis au multiple de 5 : la case
 * centrale tombait alors a cote du montant renvoye par l'API - 84 239 la ou le
 * serveur disait 84 235 - et la matrice ne concordait plus avec la carte
 * "Ma treso disponible", qui lit la meme source.
 *
 * Les axes portent donc eux aussi les valeurs du serveur, telles quelles.
 * L'arrondi ne vit plus que dans l'affichage, ou `formaterEntier` et
 * `formaterRendement` presentent ces valeurs sans les alterer.
 */

import { coefficientRendement, uniteRendement } from '@/utils/rendement'

/** Position de l'adherent sur chacun des deux axes. */
const INDEX_ADHERENT = 2

/** Nombre de valeurs par axe, cote serveur comme a l'ecran. */
const NOMBRE_VALEURS_AXE = 5

/**
 * En deca de cet ecart a l'equilibre, la case reste blanche : ni gain ni perte
 * significatifs.
 */
const SEUIL_NEUTRE_EN_EURO_PAR_HECTARE = 100

/** Palette de la maquette. Le ton clair est le ton fonce a 50 % sur blanc. */
const ORANGE_CLAIR = '#F5B283'
const ORANGE_FONCE = '#EC6608'
const VERT_CLAIR = '#7FB79D'
const VERT_FONCE = '#01713C'

const TEXTE_SUR_FOND_CLAIR = 'var(--gris-fonce)'
const TEXTE_SUR_FOND_COLORE = '#FFFFFF'

/**
 * Lit un axe de la reponse : ses cles, triees numeriquement.
 *
 * Les cles sont triees NUMERIQUEMENT, jamais lues dans leur ordre d'arrivee :
 * JavaScript remonte en tete les cles d'objet qui ressemblent a des entiers, et
 * sur {"133.6": ..., "167": ...} l'adherent ne serait plus au milieu.
 *
 * @param {object} parCle tranche de la matrice, indexee par valeur d'axe
 * @returns {number[]|null} les valeurs de l'axe, croissantes, ou null si l'axe
 *          n'a pas la taille attendue ou porte une cle illisible
 */
function lireAxe (parCle) {
  if (parCle == null) {
    return null
  }

  const valeurs = Object.keys(parCle).map(Number).sort((a, b) => a - b)

  if (valeurs.length !== NOMBRE_VALEURS_AXE || valeurs.some(isNaN)) {
    return null
  }

  return valeurs
}

/**
 * Construit la matrice affichable a partir de la reponse du serveur.
 *
 * Les montants sont recopies, jamais recalcules : la case centrale doit redonner
 * au centime pres la tresorerie que la carte "Ma treso disponible" affiche, les
 * deux lisant le meme calcul serveur.
 *
 * Seul l'axe des rendements est transforme, et uniquement d'unite : le serveur
 * compte en quintaux par hectare, l'ecran affiche des tonnes pour la betterave
 * et la pomme de terre, des kilos de filasse pour le lin fibre. Ces regles ne
 * sont pas redefinies ici, elles sont empruntees a `utils/rendement` pour que
 * les deux ecrans ne puissent pas diverger.
 *
 * @param {object} matriceGainTresorerie reponse de /tresorerie/{culture}/matrice
 * @param {object} typeCulture culture concernee, qui porte l'unite d'affichage
 * @returns {object|null} axes, unite, cellules et position de l'adherent, ou
 *          null si la reponse ne porte pas de matrice exploitable
 */
export function construireMatriceGain (matriceGainTresorerie, typeCulture) {
  const margeParPrixVente = matriceGainTresorerie?.margeSecuriteParRendementParPrixVente

  const prixVente = lireAxe(margeParPrixVente)

  if (prixVente == null) {
    return null
  }

  const colonneAdherent = margeParPrixVente[Object.keys(margeParPrixVente)
    .find(cle => Number(cle) === prixVente[INDEX_ADHERENT])]

  const rendementsEnQuintaux = lireAxe(colonneAdherent)

  if (rendementsEnQuintaux == null) {
    return null
  }

  /*
   * La reponse est indexee [prix][rendement], la grille se lit
   * [ligne rendement][colonne prix] : c'est ici que les deux se croisent.
   *
   * Les cles sont relues par leur valeur numerique et non par la chaine triee :
   * le serveur envoie "110.00000000000001", que `String(110.00000000000001)`
   * ne redonne pas a l'identique.
   */
  const clePrix = prixVente.map(prix => Object.keys(margeParPrixVente).find(cle => Number(cle) === prix))
  const cleRendement = rendementsEnQuintaux.map(rendement => Object.keys(colonneAdherent).find(cle => Number(cle) === rendement))

  const cellules = cleRendement.map(cleLigne =>
    clePrix.map(cleColonne => margeParPrixVente[cleColonne]?.[cleLigne] ?? null))

  if (cellules.some(ligne => ligne.some(valeur => valeur == null))) {
    return null
  }

  const coefficientAffichage = coefficientRendement(typeCulture)

  return {
    prixVente,
    rendements: rendementsEnQuintaux.map(rendement => rendement * coefficientAffichage),
    uniteRendement: uniteRendement(typeCulture),
    cellules,
    indexPrixVenteAdherent: INDEX_ADHERENT,
    indexRendementAdherent: INDEX_ADHERENT
  }
}

function versComposantes (couleurHexadecimale) {
  return [
    parseInt(couleurHexadecimale.slice(1, 3), 16),
    parseInt(couleurHexadecimale.slice(3, 5), 16),
    parseInt(couleurHexadecimale.slice(5, 7), 16)
  ]
}

function interpolerCouleur (couleurClaire, couleurFoncee, ratio) {
  const claire = versComposantes(couleurClaire)
  const foncee = versComposantes(couleurFoncee)
  const composantes = claire.map((valeur, index) => Math.round(valeur + (foncee[index] - valeur) * ratio))

  return `rgb(${composantes.join(', ')})`
}

/**
 * Position d'une valeur entre le seuil neutre et l'extreme de la matrice.
 *
 * L'ecart peut etre nul ou minuscule - une matrice ou tout se joue a quelques
 * euros : la teinte reste alors la plus claire, faute de quoi une case a 101
 * EUR/ha s'afficherait aussi sombre que le meilleur gain possible.
 */
function ratioIntensite (ecart, ecartMaximum) {
  if (ecartMaximum <= 0) {
    return 0
  }

  return Math.max(0, Math.min(1, ecart / ecartMaximum))
}

/**
 * Bornes de coloration de la matrice : la perte la plus lourde et le gain le
 * plus fort, qui portent les deux teintes les plus soutenues.
 *
 * @param {number[][]} cellules valeurs de la matrice
 * @returns {{perteMaximale: number, gainMaximal: number}} bornes
 */
export function bornesMatrice (cellules) {
  const valeurs = cellules.flat()

  return {
    perteMaximale: Math.min(...valeurs),
    gainMaximal: Math.max(...valeurs)
  }
}

/**
 * Fond et couleur de texte d'une case.
 *
 * @param {number} valeur tresorerie de la case, en euros par hectare
 * @param {{perteMaximale: number, gainMaximal: number}} bornes bornes de la matrice
 * @returns {{backgroundColor: string, color: string}} styles de la case
 */
export function couleurCase (valeur, bornes) {
  if (valeur == null || Math.abs(valeur) <= SEUIL_NEUTRE_EN_EURO_PAR_HECTARE) {
    return { backgroundColor: '#FFFFFF', color: TEXTE_SUR_FOND_CLAIR }
  }

  if (valeur < 0) {
    const ratio = ratioIntensite(
      Math.abs(valeur) - SEUIL_NEUTRE_EN_EURO_PAR_HECTARE,
      Math.abs(bornes.perteMaximale) - SEUIL_NEUTRE_EN_EURO_PAR_HECTARE
    )

    return {
      backgroundColor: interpolerCouleur(ORANGE_CLAIR, ORANGE_FONCE, ratio),
      color: ratio > 0.5 ? TEXTE_SUR_FOND_COLORE : TEXTE_SUR_FOND_CLAIR
    }
  }

  const ratio = ratioIntensite(
    valeur - SEUIL_NEUTRE_EN_EURO_PAR_HECTARE,
    bornes.gainMaximal - SEUIL_NEUTRE_EN_EURO_PAR_HECTARE
  )

  return {
    backgroundColor: interpolerCouleur(VERT_CLAIR, VERT_FONCE, ratio),
    color: ratio > 0.5 ? TEXTE_SUR_FOND_COLORE : TEXTE_SUR_FOND_CLAIR
  }
}

/**
 * Rendement tel qu'il se lit sur l'axe : une decimale, et pas de zero inutile.
 * 4 t/ha s'ecrit "4", 5,5 t/ha s'ecrit "5,5".
 */
export function formaterRendement (rendement) {
  if (rendement == null) {
    return ''
  }

  return String(Number(rendement.toFixed(1))).replace('.', ',')
}

/**
 * Prix et tresoreries s'ecrivent en euros entiers, sans separateur de milliers :
 * les cases mobiles font 42,5 px, un espace insecable de plus et le nombre
 * passe a la ligne.
 */
export function formaterEntier (valeur) {
  if (valeur == null) {
    return ''
  }

  return String(Math.round(valeur))
}
